/* eslint-disable yoda */
/* eslint-disable no-console */
import React, { useCallback } from 'react';
import { useToast } from '@ps-design-system/components/Toast';
import { FormattedMessage } from 'react-intl';

import {
  createListingImage,
  deleteListingImage,
  updateListingImage,
  createListingEmbedMedia,
  updateListingEmbedMediaOrder,
  deleteListingEmbedMedia,
  getListingImages
} from '../api/listingsApi';
import useUploaderStore from '../store/useUploaderStore';
import { openCloudinaryWidget } from '../utils/cloudinary.utils';
import { formatMediaForUploader, getOptimizedThumbnailUrl } from '../utils/format.utils';
import { enforceCoverPhotoRule, getNextSortOrder, isEmbedMedia } from '../utils/media.utils';
import { buildAllMedia } from '../utils/buildAllMedia.utils';
import { useUploaderPageData } from '../context/UploaderPageContext';
import { TOAST_DURATION, CLOUDINARY_IMAGE_TRANSFORMATION } from '../constants/actions.consts';
import { datadogRum } from '@datadog/browser-rum';

export const useUploaderListingsActions = () => {
  const media = useUploaderStore((state) => state.media);
  const setMedia = useUploaderStore((state) => state.setMedia);
  const addImage = useUploaderStore((state) => state.addImage);
  const updateOrAddMedia = useUploaderStore((state) => state.updateOrAddMedia);
  const removeMedia = useUploaderStore((state) => state.removeMedia);
  const { listingId, intl } = useUploaderPageData();
  const { toast } = useToast();

  const fetchMedia = useCallback(async () => {
    if (!listingId) {
      return [];
    }

    try {
      let data;
      if (listingId) {
        data = await getListingImages(listingId);
      }
      const formattedData = buildAllMedia(data);
      return formattedData;
    } catch {
      return [];
    }
  }, [listingId]);

  const handlePhotoUpload = useCallback(async () => {
    try {
      const cloudinaryResponse = await openCloudinaryWidget(intl);
      if (cloudinaryResponse) {
        const processUploads = cloudinaryResponse.map(async (fileInfo) => {
          const savedImageObject = await createListingImage({
            url: fileInfo.url.replace('upload/', `upload/${CLOUDINARY_IMAGE_TRANSFORMATION}`),
            parentId: listingId
          });
          const formmatedData = formatMediaForUploader(savedImageObject?.data?.image);
          if (formmatedData.id) {
            addImage(formmatedData);
          }
        });

        await Promise.all(processUploads);
      }
    } catch (error) {
      datadogRum.addError(error, "Error uploading photo to cloudinary", {
        listingId,
      });
    }
  }, [addImage, listingId, intl]);

  const handleEmbedUpload = useCallback(async (url) => {
    const currentMedia = useUploaderStore.getState().media;
    let sortOrder = getNextSortOrder(currentMedia);
    const payload = [{ url, sort_order: sortOrder }];

    try {
      const newEmbedItems = await createListingEmbedMedia(listingId, payload);
      let formattedNewMedia = [];

      if (newEmbedItems && newEmbedItems.length > 0) {
        formattedNewMedia = newEmbedItems.map(item => {
          let thumbnailUrl = getOptimizedThumbnailUrl(item);
          const formattedEmbed = formatMediaForUploader({ ...item, thumbnail_url: thumbnailUrl });

          if (formattedEmbed.id) {
            updateOrAddMedia(formattedEmbed);
            return formattedEmbed;
          }

          return [];
        });
      }
      return formattedNewMedia;
    } catch (error) {
      // silently fail
    }
  }, [updateOrAddMedia, listingId, media.length]);

  const handleDelete = useCallback(async (mediaItem) => {
    const isImage = mediaItem.type !== 'video' && mediaItem.type !== '3d';
    const mediaId = mediaItem.id || mediaItem._id;

    const apiCall = isImage
      ? () => deleteListingImage(mediaId)
      : () => deleteListingEmbedMedia(listingId, mediaId);

    try {
      await apiCall();
      const currentMedia = useUploaderStore.getState().media;
      const updatedAndReorderedMedia = enforceCoverPhotoRule(currentMedia, mediaId);
      setMedia(updatedAndReorderedMedia);
    } catch (error) {
      // silently fail
    }
  }, [listingId, removeMedia]);

  const handleSave = useCallback(async () => {
    const imageUpdatePromises = [];
    const embedOrderPayload = [];

    media.forEach((item, index) => {
      const sortOrder = index + 1;
      if (isEmbedMedia(item)) {
        embedOrderPayload.push({ id: item.id, sort_order: sortOrder });
      } else {
        imageUpdatePromises.push(updateListingImage({ id: item.id, payload: { sort_order: sortOrder } }));
      }
    });

    const allPromises = [...imageUpdatePromises];
    if (embedOrderPayload.length > 0) {
      allPromises.push(updateListingEmbedMediaOrder(listingId, embedOrderPayload));
    }

    try {
      await Promise.all(allPromises);

      toast({
        title: '',
        description: <FormattedMessage id="uploader.toast.success" defaultMessage="Your changes have been saved"/>,
        variant: 'white',
        duration: TOAST_DURATION
      });
    } catch (error) {
      toast({
        title: '',
        description: <FormattedMessage id="uploader.toast.save_error" defaultMessage="Failed to save changes."/>,
        variant: 'destructive',
        duration: TOAST_DURATION
      });
    }
  }, [media, toast]);

  return { fetchMedia, handlePhotoUpload, handleSave, handleDelete, handleEmbedUpload };
};
